import { TableInfo, useTable } from "api";
import { ErrorSection, Props, errTypeAndMsg } from "./HomeDashboard";
import { endpoints } from "config";
import { Spinner } from "components/Spinner/Spinner";
import { useState } from "react";
import {
  CrudItems,
  CrudOptions,
  applyMaybeBoolean,
  applyMaybeString,
  useMenuState,
} from "components/Category/CrudMenu";
import View from "assets/Icon_Action_01.png";
import { useGetPermissions } from "helpers/useGetPermissions";

import { useLocation } from "react-router-dom";
import { WorkFlowAction } from "./WorkFlowAction";
import { RightPanel } from "components/rightPanel/RightPanel";
import { WFA, WorkFlowActionItem } from "./components/WorkFlowActionItem";
import Pagination from "components/pagination/Pagination";
import { FilterBuilder } from "components/helpers/filterBuilder/FilterBuilder";
import { SearchBox } from "components/helpers/searchBox/SearchBox";
import FilterIcon from "assets/Icon_Table_Filter_01.png";
import FilterActiveIcon from "assets/Icon_Table_Filter.png";
import StartNewWorkflow from "./StartNewWorkflow";
import { allPermissions } from "App";
import { downloadUploadedFile, getFileFromURL } from "helpers/getFileFromURL";
import ToastMessage, { toastConfig } from "helpers/ToastMessage/ToastMessage";
import { toast } from "react-toastify";
import downloadIcon from "assets/downloadIcon.png";

type WorkFlowActionsProps = { access?: boolean };
export const defaultActionType: {
  menuCode: CrudOptions;
  permissionKeys: string[];
} = {
  menuCode: "REVIEW",
  permissionKeys: ["review-action", "review-approval"],
};

const crudItems: CrudItems<any> = [
  {
    icon: View,
    imgAltText: "Update",

    permissionsKey: "",
    title: "Review work flow action",
    menuCode: "REVIEW",
    showInMultiSelect: false,
    content: (
      onDone,
      selectedData,
      menu,
      additionalData,
      clickedIdIfMultiIds,
      menuActions
    ) => (
      <WorkFlowAction
        selectedData={selectedData}
        onDone={onDone}
        idFromRowOption={menu.selectedId}
        actionType="edit"
        menuActions={menuActions}
      />
    ),
    inRightPanel: true,
    isDisabled: false,
  },
  {
    icon: View,
    imgAltText: "View",
    permissionsKey: "notifications-view",
    title: "Start new workflow",
    menuCode: "VIEW",
    showInMultiSelect: false,
    content: (
      onDone,
      selectedData,
      menu,
      additionalData,
      clickedIdIfMultiIds,
      menuActions
    ) => (
      <StartNewWorkflow
        actionType={"view"}
        selectedData={selectedData}
        onDone={onDone}
        idFromRowOption={menu.selectedId}
      />
    ),
    inRightPanel: true,
    isDisabled: false,
  },
];

export const WorkFlowActions = (props: WorkFlowActionsProps & Props) => {
  const table = useTable<any, Error>({
    url: endpoints.home.workflowactions,
    queryParams: undefined,
  });

  const { loading, data, error } = table;

  const [selectedIds, setSelectedIds] = useState<any[]>([]);

  const selectedData: WFA[] =
    data?.details?.listData.filter((d: WFA) =>
      d.communications__Id // Communication Notificaiton
        ? selectedIds.some(
            ele => String(d.communications__Id) === String(ele.id)
          )
        : d.restrictedWorkflow__bWorkflowAction //userActionApprovals__bWorkflowAction
        ? selectedIds.some(
            ele =>
              String(
                d.workflowActionInstances__Id //userActionApprovals__WorkflowActionInstancesID
              ) === String(ele.id) && ele.type === "Action"
          )
        : selectedIds.find(
            el =>
              String(
                d.workflowApprovalInstances__Id //userActionApprovals__WorkflowApprovalInstancesID
              ) === String(el.id) && el.type === "Approval"
          )
    ) ?? [];

  const permissions = useGetPermissions(
    allPermissions,
    useLocation() //'/home' or '/homedashboard' or '/workflowactions'
  );

  const [menu, menuActions] = useMenuState<WFA>({
    menuItems: crudItems,
    selectedData,
    permissions,
    table: table as TableInfo,
    // idColumn,
  });

  const openMenu = ({ menuCode }: { menuCode?: CrudOptions }) => ({
    id,
    type,
  }: {
    id: number;
    type?: "Action" | "Approval" | "Notification";
  }) => {
    setSelectedIds([{ id, type }]);
    menuActions.openMenu(menuCode ?? defaultActionType.menuCode, null);
  };
  const closeMenu = () => {
    menuActions.closeMenus();
    setSelectedIds([]);
    // table.reload();
  };
  const closeMenuAndReloadTable = () => {
    closeMenu();
    table.reload();
  };

  const { menuItems, currentMenu } = menu;

  const currentItem = !currentMenu
    ? null
    : crudItems.find(
        item => item.menuCode?.toLowerCase() === currentMenu?.toLowerCase()
      );
  const crudMenuSelectedData: WFA[] =
    selectedData.length > 0 ? selectedData : menu.selectedData;

  const title = () => {
    if (!currentMenu) {
      return "";
    } else if (currentMenu === "FILTERS") {
      return "Filters";
    } else if (!currentItem) {
      return "";
    }
    if (currentMenu === "REVIEW") {
      return (
        <TitleElement
          name={workflowInstances__Name}
          // instruction={userActionApprovals__MessageText}
          // idCode={"PR" + String(sites__Id ?? "")}
          // text={sites__SitesName}
        />
      );
    }
    return applyMaybeString(currentItem.title, crudMenuSelectedData);
  };

  const content = () => {
    if (currentMenu === "FILTERS")
      return (
        <FilterBuilder
          onDone={closeMenuAndReloadTable}
          table={table as TableInfo}
          hiddenFields={[]}
          handleCancel={menuActions.closeMenus}
        />
      );
    if (!currentMenu || !currentItem) {
      return null;
    }
    if (crudItems) {
      return currentItem.content(
        closeMenuAndReloadTable,
        crudMenuSelectedData,
        menu,
        undefined,
        undefined,
        menuActions
      );
    }
  };

  const rightPanelOpen =
    crudItems && currentMenu
      ? applyMaybeBoolean(
          currentItem?.inRightPanel ?? true,
          crudMenuSelectedData
        )
      : false;

  const isDisabled = (permissionKey?: string) =>
    menuItems?.menuItems?.find(
      menuItem => menuItem.permissionKey === permissionKey
    )?.disabled;

  const isReviewDisabled = (data: WFA) =>
    data.restrictedWorkflow__bWorkflowAction
      ? isDisabled(defaultActionType.permissionKeys[0])
      : isDisabled(defaultActionType.permissionKeys[1]);

  const isLargeRightPanel = (menuCode: CrudOptions[]) =>
    menuCode.includes(currentItem?.menuCode ?? currentMenu);

  const userActionApprovals__MessageText =
    crudMenuSelectedData[0]?.restrictedWorkflow__MessageText;
  const sites__Id = crudMenuSelectedData[0]?.sites__Id;
  const sites__SitesName = crudMenuSelectedData[0]?.sites__SitesName;
  const workflowInstances__Name =
    crudMenuSelectedData[0]?.workflowInstances__Name;

  const headerElementClearFilters = () => {
    table.data?.actions.setFilters([]);
    closeMenu();
  };

  const showClearFiltersButton =
    table.data?.details.filters &&
    Array.isArray(table.data?.details.filters) &&
    table.data?.details.filters.length > 0;

  const handleOnClickdownloadActions = () => {
    getFileFromURL(endpoints.home.listCsv, "workflow_actions_list.csv")
      .then(file => {
        downloadUploadedFile(file);
      })
      .then(() =>
        toast(
          <ToastMessage
            toastData={[
              {
                title: "Download success",
                text: "Downloaded workflow actions",
              },
            ]}
            className="ToastSuccess"
          />,
          {
            ...toastConfig,
          }
        )
      )
      .catch(error => {
        console.log("download workflow actions error: ", error);

        toast(
          <ToastMessage
            toastData={[
              {
                title: "Download error",
                text: "Failed to downloaded workflow actions",
              },
            ]}
            className="ToastSuccess"
          />,
          {
            ...toastConfig,
          }
        );
      });
  };

  return (
    <div className="HomeSection WorkFlowActionsSection">
      <div className="SectionHeader">
        <h4>WORKFLOW ACTIONS</h4>

        {!loading && !error && props.access && (
          <div className="HeaderActionsContainer">
            {(table?.data?.details as any)?.bHasSearchField && (
              <SearchBox
                key={
                  data?.details.searchString ??
                  "HomeModuleWorkFlowActionsSearchBox"
                }
                onSearchStringChange={data?.actions.changeSearch}
                initialSearchString={data?.details.searchString ?? ""}
                goToPage={table.data?.actions.goToPage}
                loading={table?.loading}
              />
            )}

            <button
              className="Btn BtnDownload"
              onClick={handleOnClickdownloadActions}
            >
              <img
                src={downloadIcon}
                alt="download"
                className="DownloadBtnIcon"
              />
              <div className="Text">Download</div>
            </button>

            <div
              className="FilterButtonContainer"
              onClick={() => {
                menuActions.openMenu("FILTERS", null);
              }}
            >
              <img
                className={`FilterIcon`}
                src={
                  table.data && table.data.details.filters.length > 0
                    ? FilterActiveIcon
                    : FilterIcon
                }
                alt="Filter"
              />
            </div>
          </div>
        )}
      </div>
      {error || !props.access ? (
        <ErrorSection style={{ minHeight: "140px", height: "280px" }}>
          {!props.access
            ? errTypeAndMsg.noAccessPermission
            : errTypeAndMsg.generic}
        </ErrorSection>
      ) : data && !loading && props.access ? (
        <>
          <div className="TableHeader">
            <div className="ColumnHeader">
              <div className="Text">Workflow Name</div>
            </div>
            <div className="ColumnHeader">
              <div className="Text">Site</div>
            </div>
            <div className="ColumnHeader">
              <div className="Text">Principle ID</div>
            </div>
            <div className="ColumnHeader">
              <div className="Text">Client ID</div>
            </div>
            <div className="ColumnHeader">
              <div className="Text">Project Type</div>
            </div>
            <div className="ColumnHeader">
              <div className="Text">Programme</div>
            </div>
            <div className="ColumnHeader">
              <div className="Text">Received</div>
            </div>
          </div>

          <div className="WorkFlowActions">
            {data.details?.listData?.length === 0 ? (
              <div className="NoDataPlaceholder" style={{ height: "280px" }}>
                <p>No Workflow Actions</p>
              </div>
            ) : (
              data.details?.listData?.map((d, i) => {
                return (
                  <WorkFlowActionItem
                    key={i}
                    wfa={d}
                    openMenu={openMenu({
                      menuCode: defaultActionType.menuCode as CrudOptions,
                    })}
                    closeMenu={closeMenu}
                    isDisabled={isDisabled}
                    isReviewDisabled={isReviewDisabled}
                  />
                );
              })
            )}
          </div>
          <Pagination table={table} />

          <RightPanel
            title={title()}
            rightPanelContent={rightPanelOpen}
            headerElement={
              currentMenu === "FILTERS" ? (
                <div
                  className={`ClearFilters ${
                    !showClearFiltersButton ? "Hide" : ""
                  }`}
                >
                  <div className="Btn" onClick={headerElementClearFilters}>
                    <div className="Text">Clear Filters</div>
                  </div>
                </div>
              ) : (
                undefined
              )
            }
            closePanel={closeMenu}
            largeRightPanel={
              currentMenu === "FILTERS"
                ? false
                : isLargeRightPanel([defaultActionType.menuCode, "VIEW"])
            }
          >
            {content()}
          </RightPanel>
        </>
      ) : (
        <div
          style={{
            height: "280px",
          }}
          className="HomeSectionLoading"
        >
          <Spinner positionAbsolute />
        </div>
      )}
    </div>
  );
};

const TitleElement = ({
  name,
  instruction,
  idCode,
  text,
}: {
  name?: string | null;
  instruction?: string | null;
  idCode?: string;
  text?: string | null;
}) => {
  return (
    <div className="ReviewWorkActionTitle">
      <span className="Name">{name}</span>
      {instruction && <span className="Instruction">{instruction}</span>}
      {idCode && <span className="idCode">{idCode}</span>}
      {text && <span className="TextName">{text}</span>}
    </div>
  );
};
